import { callApi } from "./api";
import { getUserDocuments } from "./api"; // Import for getPrimaryResumeContent

const aiService = {
  getJobCategoriesAndStartSearch: async (email, resumeText, location) => {
    // YOUR ORIGINAL LOGIC FOR THIS FUNCTION
    console.log("[aiService] getJobCategoriesAndStartSearch called...");
    if (!email || !location) {
      throw new Error("Email and location are required.");
    }
    const formData = new FormData();
    formData.append("email", email);
    formData.append("location", location);

    if (
      resumeText &&
      typeof resumeText === "string" &&
      resumeText.trim().length >= 50
    ) {
      console.log("[aiService] Sending provided resume text to backend.");
      formData.append("resume", resumeText);
    } else {
      console.log(
        "[aiService] No valid resume text provided. Backend should use profile data."
      );
    }

    try {
      const response = await callApi(
        "POST",
        "/prompt/job-categories",
        formData
      );
      return response;
    } catch (error) {
      console.error(
        "[aiService] Error in getJobCategoriesAndStartSearch:",
        error.data || error.message
      );
      throw error;
    }
  },

  generateMasterResume: async (sessionId) => {
    // YOUR ORIGINAL LOGIC FOR THIS FUNCTION
    console.log(`[aiService] generateMasterResume session: ${sessionId}`);
    if (!sessionId) throw new Error("Session ID required for master resume.");
    const formData = new FormData();
    formData.append("session_id", sessionId);
    try {
      const response = await callApi("POST", "/prompt/master-resume", formData);
      return response;
    } catch (error) {
      console.error(
        "[aiService] Error generating master resume:",
        error.data || error.message
      );
      throw error;
    }
  },

  // ==============================================================================
  //  NEW FUNCTION FOR STRUCTURED PREMIUM RESUME
  // ==============================================================================
  /**
   * Calls the backend to generate a structured JSON resume for premium templates.
   * @param {string} sessionId - The active AI session ID.
   * @returns {Promise<object>} A promise that resolves to the structured resume data object.
   */
  generateStructuredResume: async (sessionId) => {
    console.log(`[aiService] generateStructuredResume session: ${sessionId}`);
    if (!sessionId) {
      throw new Error(
        "Session ID is required to generate a structured resume."
      );
    }

    // Use FormData to match the backend's `Form(...)` expectation.
    const formData = new FormData();
    formData.append("session_id", sessionId);

    try {
      // Use your existing callApi helper for consistency.
      // The endpoint matches the new route in your routes.py.
      const response = await callApi(
        "POST",
        "/prompt/structured-resume",
        formData
      );
      return response;
    } catch (error) {
      // Log the error and re-throw it in the same pattern as your other functions.
      console.error(
        "[aiService] Error generating structured resume:",
        error.data || error.message
      );
      throw error;
    }
  },
  // ==============================================================================

  pollTaskStatus: async (taskId, retries = 10, delay = 3000) => {
    // YOUR ORIGINAL LOGIC FOR THIS FUNCTION
    console.warn("pollTaskStatus in aiService is not fully implemented yet.");
    return Promise.resolve({ status: "POLLING_NOT_IMPLEMENTED" });
  },

  optimizeResumeForJob: async (resumeContent, jobDetailsText, sessionId) => {
    console.log(`[aiService] optimizeResumeForJob session: ${sessionId}`);
    console.log(
      `[aiService] Resume content: ${
        resumeContent
          ? `${resumeContent.length} chars`
          : "null/empty (will use profile)"
      }`
    );
    console.log(
      `[aiService] Job details: ${
        jobDetailsText ? `${jobDetailsText.length} chars` : "missing"
      }`
    );

    // CHANGED: Remove resumeContent from validation - allow it to be empty for profile fallback
    if (!jobDetailsText || !sessionId) {
      throw new Error(
        "Job details and session ID are required for optimizeResumeForJob."
      );
    }

    const formData = new FormData();
    // CHANGED: Send empty string if resumeContent is null/undefined
    formData.append("master_resume", resumeContent || "");
    formData.append("job_details", jobDetailsText);
    formData.append("session_id", sessionId);

    try {
      const response = await callApi(
        "POST",
        "/prompt/optimize-resume",
        formData
      );
      return response;
    } catch (error) {
      console.error(
        "[aiService] Error optimizing resume:",
        error.data || error.message
      );
      throw error;
    }
  },

  generateCoverLetter: async (
    optimizedResumeText,
    jobDetailsText,
    sessionId
  ) => {
    // YOUR ORIGINAL LOGIC FOR THIS FUNCTION
    console.log(`[aiService] generateCoverLetter session: ${sessionId}`);
    if (!optimizedResumeText || !jobDetailsText || !sessionId)
      throw new Error("Missing required arguments for generateCoverLetter.");
    const formData = new FormData();
    formData.append("optimized_resume", optimizedResumeText);
    formData.append("job_details", jobDetailsText);
    formData.append("session_id", sessionId);
    try {
      const response = await callApi("POST", "/prompt/cover-letter", formData);
      return response;
    } catch (error) {
      console.error(
        "[aiService] Error generating cover letter:",
        error.data || error.message
      );
      throw error;
    }
  },

  getCourseRecommendations: async (sessionId) => {
    // YOUR ORIGINAL LOGIC FOR THIS FUNCTION
    console.log(`[aiService] getCourseRecommendations session: ${sessionId}`);
    if (!sessionId) throw new Error("Session ID required for recommendations.");
    const formData = new FormData();
    formData.append("session_id", sessionId);
    try {
      const recommendations = await callApi(
        "POST",
        "/prompt/course-recommendations",
        formData
      );
      if (!Array.isArray(recommendations)) {
        console.error(
          "[aiService] Non-array course response:",
          recommendations
        );
        throw new Error("Unexpected course format.");
      }
      return recommendations;
    } catch (error) {
      console.error(
        "[aiService] Error getting courses:",
        error.data || error.message
      );
      throw error;
    }
  },

  getPrimaryResumeContent: async () => {
    // YOUR ORIGINAL LOGIC FOR THIS FUNCTION
    console.log("[aiService] Fetching primary resume content...");
    try {
      const documents = await getUserDocuments();
      if (!documents || documents.length === 0) {
        console.warn("[aiService] No documents found for user.");
        throw new Error("No resumes found for the user.");
      }
      const primaryResume = documents.find(
        (doc) => doc.is_primary && doc.document_type === "resume"
      );
      if (!primaryResume) {
        console.warn("[aiService] No primary 'resume' document found.");
        throw new Error("No primary resume found.");
      }
      console.log(
        `[aiService] Found primary resume with ID: ${primaryResume.id}. Fetching its content.`
      );
      const contentResponse = await callApi(
        "GET",
        `/profile/resumes/${primaryResume.id}/content`,
        null,
        {
          headers: { Accept: "text/plain, application/json;q=0.8" },
          responseType: "text",
        }
      );
      if (typeof contentResponse === "string") {
        if (contentResponse.trim().length < 10 && contentResponse.length > 0) {
          console.warn("[aiService] Primary resume content is very short.");
        }
        return contentResponse;
      } else {
        console.error(
          "[aiService] Unexpected primary resume response structure. Expected plain text, got:",
          typeof contentResponse,
          contentResponse
        );
        throw new Error(
          "Invalid data format for primary resume content. Expected plain text."
        );
      }
    } catch (error) {
      console.error(
        "[aiService] Error fetching primary resume content:",
        error.status,
        error.data || error.message
      );
      if (error.status === 404) {
        throw new Error(
          error.data?.detail || "Primary resume or its content not found."
        );
      }
      throw new Error(
        error.data?.detail ||
          error.message ||
          "Failed to load primary resume content."
      );
    }
  },
};

export default aiService;
